import { useQuery } from "@tanstack/react-query";
import { GlassCard } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ShieldCheck } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { getDeviceManagedApps } from "@/services/devicesApi";
import { getAppStatusMeta } from "@/utils/appStatus";
import type { DeviceManagedAppDTO } from "@/types/api";

interface ManagedApplicationsProps {
  deviceId: string;
}

const getName = (a: DeviceManagedAppDTO): string =>
  (a.name || a.appName || a.bundleIdentifier || a.identifier || "Unknown App").replace(
    /\.app$/,
    "",
  );

const getBundleId = (a: DeviceManagedAppDTO): string =>
  a.bundleIdentifier || a.identifier || "";

const getVersion = (a: DeviceManagedAppDTO): string =>
  a.version || a.shortVersion || "N/A";

const getState = (a: DeviceManagedAppDTO): string | null | undefined =>
  a.state || a.installState || a.status;

const renderIcon = (size: "sm" | "md") => {
  const dim = size === "sm" ? "h-7 w-7" : "h-8 w-8";
  const inner = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <div
      className={cn(
        dim,
        "rounded bg-primary/10 flex items-center justify-center flex-shrink-0",
      )}
    >
      <ShieldCheck className={cn(inner, "text-primary")} />
    </div>
  );
};

const ManagedApplications = ({ deviceId }: ManagedApplicationsProps) => {
  const isMobile = useIsMobile();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["device-managed-apps", deviceId],
    queryFn: () => getDeviceManagedApps(deviceId),
    enabled: !!deviceId,
    staleTime: 1000 * 30,
    retry: 1,
  });

  const apps = Array.isArray(data) ? data : [];
  const sorted = [...apps].sort((a, b) =>
    getName(a).localeCompare(getName(b)),
  );

  return (
    <GlassCard
      variant="default"
      hover={false}
      className={cn(isMobile ? "p-3" : "p-6")}
    >
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h2
          className={cn(
            "font-semibold text-slate-900 dark:text-white",
            isMobile ? "text-base" : "text-xl",
          )}
        >
          Managed Applications {!isLoading && `(${apps.length})`}
        </h2>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <div className={cn("text-center", isMobile ? "py-6" : "py-8")}>
          <ShieldCheck
            className={cn(
              "mx-auto mb-3 text-muted-foreground",
              isMobile ? "h-8 w-8" : "h-12 w-12",
            )}
          />
          <p className="font-medium mb-1">Unable to load managed apps</p>
          <p className="text-sm text-muted-foreground">
            {(error as Error)?.message ||
              "We couldn't reach the backend. Please try again in a moment."}
          </p>
        </div>
      ) : apps.length === 0 ? (
        <div
          className={cn(
            "text-center text-slate-500",
            isMobile ? "py-6" : "py-8",
          )}
        >
          <ShieldCheck
            className={cn(
              "mx-auto mb-3 text-slate-400",
              isMobile ? "h-8 w-8" : "h-12 w-12",
            )}
          />
          <p
            className={cn(
              "font-medium mb-2",
              isMobile ? "text-base" : "text-lg",
            )}
          >
            No Managed Apps
          </p>
          <p className="text-sm text-slate-400">
            No managed apps have been deployed to this device yet. Once you
            deploy an app, its install status will appear here.
          </p>
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sorted.map((app, idx) => {
            const status = getAppStatusMeta(getState(app));
            return (
              <div
                key={`${getBundleId(app)}-${idx}`}
                className="flex items-center gap-2 p-3 rounded-lg border bg-card/50 min-h-[44px]"
              >
                {renderIcon("sm")}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{getName(app)}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {getBundleId(app) || "—"} · {getVersion(app)}
                  </p>
                </div>
                <Badge className={cn("flex-shrink-0", status.className)}>
                  {status.label}
                </Badge>
              </div>
            );
          })}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-left">Application</TableHead>
              <TableHead className="text-left">Status</TableHead>
              <TableHead className="text-left">Version</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((app, idx) => {
              const status = getAppStatusMeta(getState(app));
              return (
                <TableRow key={`${getBundleId(app)}-${idx}`}>
                  <TableCell className="text-left">
                    <div className="flex items-center">
                      <div className="mr-3">{renderIcon("md")}</div>
                      <div className="min-w-0">
                        <div className="font-medium">{getName(app)}</div>
                        {getBundleId(app) && (
                          <div className="text-xs text-muted-foreground truncate">
                            {getBundleId(app)}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-left">
                    <Badge className={status.className}>{status.label}</Badge>
                  </TableCell>
                  <TableCell className="text-left">{getVersion(app)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </GlassCard>
  );
};

export default ManagedApplications;
